import {
    CheckCircle as CheckCircleIcon,
    DoneRounded as DoneRoundedIcon,
    ErrorRounded as ErrorRoundedIcon,
} from 'components/icons';
import { Progress as ProgressPrimitive } from '@base-ui/react/progress';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import PermissionButton from 'components/buttons/PermissionButton';
import CircularLoader from 'components/CircularLoader';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import Loader from 'components/Loader';
import { INLINE_SPINNER_CLASS } from 'components/processes/ProcessCard';
import { Alert } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Card, CardContent } from 'components/ui/card';
import { ProgressIndicator, ProgressTrack } from 'components/ui/progress';
import {
    useIsMatterSyncInProgressQuery,
    useLazyIsMatterSyncInProgressQuery,
    useSyncMattersMutation,
} from 'store/api/tiq-time-integration';
import { cn } from 'utils/cn';

type MatterSyncForm = {
    limit: { label: string; value: number } | null;
};

const limitOptions = [
    { label: '1', value: 1 },
    { label: '10', value: 10 },
    { label: '100', value: 100 },
    { label: '500', value: 500 },
    { label: '1000', value: 1000 },
    { label: 'All', value: 0 },
];

const ALERT_CLASS =
    'text-fg flex items-center gap-2 rounded-lg py-2 pr-4 pl-3 text-sm font-semibold *:[svg]:text-primary';

const MatterSync: FC = () => {
    const [defaultValues] = useState<MatterSyncForm>({ limit: null });
    const [syncMatters, { isLoading: isSyncLoading, error, isSuccess, reset }] = useSyncMattersMutation();
    const [lastSyncedLimit, setLastSyncedLimit] = useState<number | null>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Initial check on page load
    const { data: isInitiallyInProgress, isLoading: isCheckingInitialStatus } = useIsMatterSyncInProgressQuery();

    // Lazy query for polling
    const [checkProgress, { data: isInProgress }] = useLazyIsMatterSyncInProgressQuery();

    const formMethods = useForm<MatterSyncForm>({
        defaultValues,
        values: defaultValues,
    });
    const { control, setValue } = formMethods;

    const selectedLimit = useWatch({ control, name: 'limit' });

    // Determine current sync status
    const currentlyInProgress = isInitiallyInProgress || isInProgress || false;
    const isSyncDisabled = selectedLimit == null || currentlyInProgress;

    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        pollingIntervalRef.current = setInterval(() => {
            checkProgress();
        }, 5000); // Poll every 5 seconds
    }, [checkProgress]);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = undefined;
        }
    }, []);

    // Start polling when sync is initiated or when page loads with sync in progress
    useEffect(() => {
        if (currentlyInProgress) {
            startPolling();
        } else {
            stopPolling();
        }

        return () => stopPolling();
    }, [currentlyInProgress, startPolling, stopPolling]);

    // Stop polling when sync completes
    useEffect(() => {
        if (isInProgress === false && pollingIntervalRef.current) {
            stopPolling();
        }
    }, [isInProgress, stopPolling]);

    const handleSync = useCallback(async () => {
        if (selectedLimit == null) return;

        try {
            setLastSyncedLimit(selectedLimit.value === 0 ? null : selectedLimit.value);
            await syncMatters(selectedLimit.value === 0 ? undefined : selectedLimit.value).unwrap();
            // Polling will be started by the useEffect when currentlyInProgress becomes true
        } catch (err) {
            console.error('Sync failed:', err);
            setLastSyncedLimit(null);
        }
    }, [selectedLimit, syncMatters]);

    // Reset success/error states when limit changes
    useEffect(() => {
        if (!currentlyInProgress) {
            reset();
        }
    }, [selectedLimit, currentlyInProgress, reset]);

    if (!defaultValues || isCheckingInitialStatus) {
        return <Loader />;
    }

    return (
        <Card className="gap-0 py-0">
            <CardContent className="flex flex-col gap-4 p-5">
                <div className="flex items-center justify-between">
                    <h2 className="text-h6">
                        <FormattedMessage id="tiqtime.matter-sync.title" />
                    </h2>

                    {isSuccess && !currentlyInProgress && (
                        <Badge className="bg-success text-primary-foreground h-8 gap-1.5 rounded-full px-3 text-sm [&>svg]:size-5!">
                            <CheckCircleIcon />
                            <FormattedMessage id="tiqtime.matter-sync.status-completed" />
                        </Badge>
                    )}
                </div>

                <p className="text-body1 text-fg-secondary">
                    <FormattedMessage id="tiqtime.matter-sync.description" />
                </p>

                {currentlyInProgress && (
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <CircularLoader className={INLINE_SPINNER_CLASS} />
                            <p className="text-body2 text-fg-secondary">
                                <FormattedMessage
                                    id="tiqtime.matter-sync.progress-message"
                                    values={{
                                        count: lastSyncedLimit || 'all',
                                    }}
                                />
                            </p>
                        </div>
                        <ProgressPrimitive.Root value={null}>
                            <ProgressTrack className="h-1.5 rounded">
                                <ProgressIndicator className="loading-bar-sweep w-full rounded transition-none" />
                            </ProgressTrack>
                        </ProgressPrimitive.Root>
                    </div>
                )}

                <div className="flex flex-wrap gap-2 self-start">
                    <FormProvider {...formMethods}>
                        <SelectInput
                            setValue={setValue}
                            control={control}
                            name="limit"
                            options={limitOptions}
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            wrapperClassName="max-w-fit min-w-[120px]"
                            placeholder="tiqtime.matter-sync.select-limit"
                            gap={0}
                            disabled={currentlyInProgress}
                        />
                    </FormProvider>

                    <PermissionButton
                        permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                        variant="contained"
                        color="primary"
                        disabled={isSyncDisabled}
                        onClick={() => {
                            void handleSync();
                        }}
                        startIcon={
                            isSyncLoading ? (
                                <CircularLoader className={cn(INLINE_SPINNER_CLASS, '[&>span]:size-5')} />
                            ) : null
                        }
                    >
                        <FormattedMessage
                            id={
                                currentlyInProgress
                                    ? 'tiqtime.matter-sync.in-progress'
                                    : 'tiqtime.matter-sync.sync-button'
                            }
                        />
                    </PermissionButton>
                </div>

                {error && !currentlyInProgress && (
                    <Alert className={cn(ALERT_CLASS, 'border-error bg-error-light')}>
                        <ErrorRoundedIcon />
                        <FormattedMessage
                            id={
                                'status' in error && error.status === 409
                                    ? 'tiqtime.matter-sync.error-already-running'
                                    : 'tiqtime.matter-sync.error-general'
                            }
                        />
                    </Alert>
                )}
                {isSuccess && !currentlyInProgress && (
                    <Alert className={cn(ALERT_CLASS, 'border-success bg-success-light')}>
                        <DoneRoundedIcon />
                        <FormattedMessage
                            id="tiqtime.matter-sync.success"
                            values={{
                                count: lastSyncedLimit || 'all',
                            }}
                        />
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

export default MatterSync;
